import { CommonModule, isPlatformBrowser } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { MatIconModule } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { InputComponent, InputEmittedValue } from "@corp-ais/omniark-ui";
import { asyncScheduler, filter, Subject, takeUntil } from "rxjs";
import { observeOn } from "rxjs/operators";
import { enrichmentMenuPath } from "@app/feature/management/enrichment-mode/enrichment-mode.url";
import { MenuNode } from "../../core/models/menu-meta";
import { AuthService, UserProfile } from "../../core/services/auth.service";
import { CommonService } from "../../core/services/common.service";
import { RouterMenuService } from "../../core/services/router-menu.service";
import {
  SideNav,
  buildItemsFromMenu,
  ISideNavItem,
  ISideNavItemSelectEvent,
} from "../../shared/components/side-nav";
import { IMenuItem } from "../../shared/models/common.model";

@Component({
  selector: "app-menu-layout",
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    FormsModule,
    InputComponent,
    SideNav,
  ],
  templateUrl: "./menu-layout.html",
  styleUrl: "./menu-layout.scss",
})
export class MenuLayout implements OnInit, OnDestroy {
  @Input() isMobile = false;
  @Input() isOpen = false;
  @Output() menuToggled = new EventEmitter<boolean>();

  menuCollapsed = false;
  menuToggleIconTurns = 0;
  isMenuWidthAnimating = false;
  menuReady = false;
  menuTree: IMenuItem[] = [];
  user: UserProfile | null = null;
  processedMenuItems: IMenuItem[] = [];
  activeMenuPath = "";
  searchTerm = "";
  isOnline = navigator.onLine;

  readonly dashboardPinnedItems: ISideNavItem[] = [
    {
      id: "/",
      label: "Dashboard",
      icon: "dashboard",
      path: "/",
      dividerAfter: true,
    },
  ];

  private destroy$ = new Subject<void>();
  private menuWidthAnimTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly menuWidthMotionMs = 360;

  private readonly authService = inject(AuthService);
  private readonly commonService = inject(CommonService);
  private readonly routerMenuService = inject(RouterMenuService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly menu$ = toObservable(this.routerMenuService.menu).pipe(
    observeOn(asyncScheduler),
  );

  get sideNavItems(): ISideNavItem[] {
    return buildItemsFromMenu(this.processedMenuItems);
  }

  get currentUrlPath(): string {
    return this.currentPath();
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.checkInitialAuthentication();

      window.addEventListener("online", () => {
        this.isOnline = true;
      });
      window.addEventListener("offline", () => {
        this.isOnline = false;
      });

      this.menu$.pipe(takeUntil(this.destroy$)).subscribe((tree) => {
        this.initMenu(tree, this.routerMenuService.ready());
      });

      this.authService.user$
        .pipe(takeUntil(this.destroy$))
        .subscribe((user) => {
          this.initUser(user);
        });

      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd,
          ),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.syncActiveMenuFromRouter();
        });
    }
  }

  ngOnDestroy(): void {
    this.clearMenuWidthAnimTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  initMenu(menuNodes: readonly MenuNode[], menuLoaded: boolean) {
    this.menuTree = menuNodes.map((node) => menuNodeToItem(node));
    this.applyFilters();
    this.checkMenuReady(menuLoaded);
    this.activeMenuPath = this.currentPath();
    sessionStorage.setItem("activeMenuPath", this.activeMenuPath);
  }

  initUser(user: UserProfile | null) {
    this.user = user;
  }

  private checkInitialAuthentication() {
    if (
      this.authService.getAccessToken() &&
      !this.authService.isUserDataLoaded()
    ) {
      this.authService.getProfileAndMenu().subscribe();
    }
  }

  private checkMenuReady(menuLoaded: boolean) {
    if (menuLoaded) {
      this.menuReady = true;
      this.commonService.setAppInitialized(true);
    }
  }

  toggleMenuCollapse() {
    this.isMenuWidthAnimating = true;
    this.menuCollapsed = !this.menuCollapsed;
    this.menuToggleIconTurns += 1;
    this.menuToggled.emit(this.menuCollapsed);
    this.scheduleMenuWidthAnimEnd();
  }

  onSideNavCollapsedChange(collapsed: boolean): void {
    if (this.menuCollapsed === collapsed) return;
    this.isMenuWidthAnimating = true;
    this.menuCollapsed = collapsed;
    this.menuToggleIconTurns += 1;
    this.menuToggled.emit(collapsed);
    this.scheduleMenuWidthAnimEnd();
  }

  onSideNavItemSelect(event: ISideNavItemSelectEvent): void {
    const item = event.item;
    if (item.path === "/" || item.id === "/") {
      this.goToDashboard();
      return;
    }
    this.goToRouter({
      name: item.label,
      path: item.path ?? item.id,
      icon: item.icon,
    });
  }

  private scheduleMenuWidthAnimEnd(): void {
    this.clearMenuWidthAnimTimer();
    this.menuWidthAnimTimer = setTimeout(() => {
      this.isMenuWidthAnimating = false;
      this.menuWidthAnimTimer = null;
    }, this.menuWidthMotionMs);
  }

  private clearMenuWidthAnimTimer(): void {
    if (this.menuWidthAnimTimer !== null) {
      clearTimeout(this.menuWidthAnimTimer);
      this.menuWidthAnimTimer = null;
    }
  }

  syncActiveMenuFromRouter(): void {
    this.setActiveMenu(this.currentPath());
  }

  setActiveMenu(menuPath: string) {
    this.activeMenuPath = menuPath;
    sessionStorage.setItem("activeMenuPath", menuPath);
  }

  onSearchChange(term: InputEmittedValue): void {
    this.searchTerm = String(term || "");
    this.applyFilters();
  }

  private applyFilters(): void {
    const term = this.searchTerm.trim();
    if (!term) {
      this.processedMenuItems = this.menuTree;
      return;
    }

    this.processedMenuItems = this.filterMenuTree(
      this.menuTree,
      term.toLowerCase(),
    );
  }

  private filterMenuTree(items: IMenuItem[], searchLower: string): IMenuItem[] {
    const result: IMenuItem[] = [];

    for (const item of items) {
      const matchesSelf =
        item.name.toLowerCase().includes(searchLower) ||
        item.path.toLowerCase().includes(searchLower);
      const filteredChildren = item.children
        ? this.filterMenuTree(item.children, searchLower)
        : [];

      if (matchesSelf || filteredChildren.length > 0) {
        result.push({
          ...item,
          children:
            filteredChildren.length > 0 ? filteredChildren : item.children,
        });
      }
    }

    return result;
  }

  private currentPath(): string {
    return enrichmentMenuPath(this.router.url.split("?")[0] || "/");
  }

  onLogout() {
    this.authService.logout();
  }

  goToRouter(menu: IMenuItem) {
    const navigatePath = menu.path;

    if (navigatePath.startsWith("/partner")) {
      window.location.href = navigatePath;
      return;
    }

    this.router
      .navigate([navigatePath])
      .then(() => {
        this.setActiveMenu(navigatePath);
        if (this.isMobile && this.isOpen) {
          this.menuToggled.emit(false);
        }
      })
      .catch(() => undefined);
  }

  goToDashboard(): void {
    this.router
      .navigate(["/"])
      .then(() => {
        this.setActiveMenu("/");
        if (this.isMobile && this.isOpen) {
          this.menuToggled.emit(false);
        }
      })
      .catch(() => undefined);
  }
}

function menuNodeToItem(node: MenuNode): IMenuItem {
  const children =
    node.children.length > 0
      ? node.children.map((child) => menuNodeToItem(child))
      : undefined;

  return {
    name: node.name,
    path: node.path,
    icon: node.icon,
    children,
  };
}
